import { StateCreator } from 'zustand';
import { api } from '../../api/client';

export interface Lead {
  id: string;
  _id?: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  source?: string;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'rejected';
  score: number;
  notes?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  assignedTo?: string;
  createdBy?: string;
  created_at?: string;
  updated_at?: string;
  _optimistic?: boolean;
}

export interface LeadsPagination {
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
}

export interface LeadsFilters {
  search?: string;
  status?: string[];
  source?: string[];
  assigned_to?: string;
  score_min?: number;
  score_max?: number;
}

export interface LeadsSlice {
  // State
  leads: Lead[];
  leadsLoading: boolean;
  leadsError: string | null;
  leadsPagination: LeadsPagination;
  leadsFilters: LeadsFilters;
  optimisticLeads: Map<string, Lead>;

  // CRUD Actions
  fetchLeads: (filters?: LeadsFilters) => Promise<void>;
  createLead: (data: Partial<Lead>) => Promise<Lead>;
  updateLead: (id: string, data: Partial<Lead>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;

  // Инкрементальный SSE handler
  handleLeadSSE: (event: any) => void;

  // Filters
  setLeadsFilters: (filters: LeadsFilters) => void;
  setLeadsPage: (page: number) => void;

  // Optimistic operations
  optimisticCreateLead: (tempId: string, data: Partial<Lead>) => void;
  optimisticUpdateLead: (id: string, data: Partial<Lead>) => void;
  optimisticDeleteLead: (id: string) => void;
}

export const createLeadsSlice: StateCreator<LeadsSlice> = (set, get) => ({
  // Initial state
  leads: [],
  leadsLoading: false,
  leadsError: null,
  leadsPagination: {
    page: 1,
    totalPages: 1,
    total: 0,
    perPage: 20,
  },
  leadsFilters: {
    search: '',
    status: [],
    source: [],
    assigned_to: '',
    score_min: 0,
    score_max: 100,
  },
  optimisticLeads: new Map(),

  // Fetch leads
  fetchLeads: async (filters?: LeadsFilters) => {
    const currentFilters = filters || get().leadsFilters;

    set({ leadsLoading: true, leadsError: null });

    try {
      const params: any = {
        page: get().leadsPagination.page,
        per_page: get().leadsPagination.perPage,
        ...currentFilters,
      };

      // Преобразуем массивы в CSV строки
      if (params.status && Array.isArray(params.status)) {
        params.status = params.status.join(',');
      }
      if (params.source && Array.isArray(params.source)) {
        params.source = params.source.join(',');
      }

      const response = await api.get('/leads', { params });

      set({
        leads: response.data || response.data.data || [],
        leadsPagination: {
          page: response.pagination?.page || response.page || 1,
          totalPages: response.pagination?.pages || response.pages || 1,
          total: response.pagination?.total || response.total || 0,
          perPage: response.pagination?.per_page || response.per_page || 20,
        },
        leadsLoading: false,
        leadsError: null,
      });
    } catch (error: any) {
      set({
        leadsError: error.response?.data?.message || error.message || 'Ошибка загрузки лидов',
        leadsLoading: false,
      });
    }
  },

  // Create lead with optimistic update
  createLead: async (data: Partial<Lead>) => {
    const tempId = `temp-${Date.now()}`;
    get().optimisticCreateLead(tempId, data);

    try {
      const response = await api.post('/leads', data);
      const newLead = response.data;

      // Заменяем временный lead на реальный
      set((state) => ({
        leads: state.leads.map((l) => (l.id === tempId ? newLead : l)),
        optimisticLeads: new Map(
          [...state.optimisticLeads].filter(([k]) => k !== tempId)
        ),
      }));

      return newLead;
    } catch (error: any) {
      // Откатываем оптимистичное обновление
      set((state) => ({
        leads: state.leads.filter((l) => l.id !== tempId),
        optimisticLeads: new Map(
          [...state.optimisticLeads].filter(([k]) => k !== tempId)
        ),
      }));
      throw error;
    }
  },

  // Update lead with optimistic update
  updateLead: async (id: string, data: Partial<Lead>) => {
    const original = get().leads.find((l) => l.id === id || l._id === id);
    get().optimisticUpdateLead(id, data);

    try {
      const response = await api.put(`/leads/${id}`, data);
      const updated = response.data;

      set((state) => ({
        leads: state.leads.map((l) => (l.id === id || l._id === id ? updated : l)),
        optimisticLeads: new Map(
          [...state.optimisticLeads].filter(([k]) => k !== id)
        ),
      }));
    } catch (error: any) {
      // Откатываем к оригиналу
      if (original) {
        set((state) => ({
          leads: state.leads.map((l) => (l.id === id || l._id === id ? original : l)),
          optimisticLeads: new Map(
            [...state.optimisticLeads].filter(([k]) => k !== id)
          ),
        }));
      }
      throw error;
    }
  },

  // Delete lead with optimistic update
  deleteLead: async (id: string) => {
    const original = get().leads.find((l) => l.id === id || l._id === id);
    get().optimisticDeleteLead(id);

    try {
      await api.delete(`/leads/${id}`);

      set((state) => ({
        optimisticLeads: new Map(
          [...state.optimisticLeads].filter(([k]) => k !== id)
        ),
      }));
    } catch (error: any) {
      // Восстанавливаем
      if (original) {
        set((state) => ({
          leads: [...state.leads, original],
          optimisticLeads: new Map(
            [...state.optimisticLeads].filter(([k]) => k !== id)
          ),
        }));
      }
      throw error;
    }
  },

  // SSE handler - инкрементальное обновление
  handleLeadSSE: (event) => {
    const { type, data } = event;

    switch (type) {
      case 'lead.created':
        set((state) => {
          const leadData = data.lead || data.data?.lead || data;
          const leadId = leadData.id || leadData._id;

          // Проверяем есть ли уже такой лид
          const exists = state.leads.some((l) => {
            const existingId = l.id || l._id;
            return existingId && (existingId === leadId || existingId === leadData.id || existingId === leadData._id);
          });

          if (!exists && leadId) {
            console.log('[SSE] Adding new lead:', leadId);
            return { leads: [leadData, ...state.leads] };
          }

          console.log('[SSE] Lead already exists, skipping:', leadId);
          return state;
        });
        break;

      case 'lead.updated':
        set((state) => {
          const leadData = data.lead || data.data?.lead || data;
          const leadId = leadData.id || leadData._id;

          return {
            leads: state.leads.map((l) => {
              const existingId = l.id || l._id;
              return existingId === leadId ? { ...l, ...leadData, id: leadId } : l;
            }),
          };
        });
        break;

      case 'lead.deleted':
        set((state) => {
          const leadId = data.id || data.data?.id;
          return {
            leads: state.leads.filter((l) => {
              const existingId = l.id || l._id;
              return existingId !== leadId;
            }),
          };
        });
        break;
    }
  },

  // Set filters
  setLeadsFilters: (filters: LeadsFilters) => {
    set({
      leadsFilters: filters,
      leadsPagination: { ...get().leadsPagination, page: 1 }, // ✅ Сброс на первую страницу
    });
    get().fetchLeads(filters);
  },

  // Set page
  setLeadsPage: (page: number) => {
    set((state) => ({
      leadsPagination: { ...state.leadsPagination, page },
    }));
    get().fetchLeads();
  },

  // Optimistic operations
  optimisticCreateLead: (tempId: string, data: Partial<Lead>) => {
    const tempLead: Lead = {
      id: tempId,
      name: data.name || '',
      email: data.email,
      phone: data.phone,
      company: data.company,
      status: data.status || 'new',
      score: data.score || 0,
      _optimistic: true,
    };

    set((state) => ({
      leads: [tempLead, ...state.leads],
      optimisticLeads: new Map([...state.optimisticLeads, [tempId, tempLead]]),
    }));
  },

  optimisticUpdateLead: (id: string, data: Partial<Lead>) => {
    set((state) => {
      const updated = state.leads.map((l) => {
        if (l.id === id || l._id === id) {
          return { ...l, ...data, _optimistic: true };
        }
        return l;
      });

      const lead = updated.find((l) => l.id === id || l._id === id);

      return {
        leads: updated,
        optimisticLeads: lead
          ? new Map([...state.optimisticLeads, [id, lead]])
          : state.optimisticLeads,
      };
    });
  },

  optimisticDeleteLead: (id: string) => {
    set((state) => ({
      leads: state.leads.filter((l) => l.id !== id && l._id !== id),
      optimisticLeads: new Map([...state.optimisticLeads, [id, null as any]]),
    }));
  },
});
